import { useEffect, useState, useContext, useRef, useCallback } from "react";

import { useLazyQuery } from "@apollo/client";
import Geocode from "react-geocode";
import { useParams } from "react-router-dom";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
  MarkerClusterer,
} from "@react-google-maps/api";

import { ACTIVE_INCIDENT_QUERY } from "../../Queries/ActiveIncidentQuery";
import { MAPPABLE_OFFICERS } from "../../Queries/MappableOfficers";

import { UserContext } from "../../Contexts/UserContext";
import { useToggle } from "../../CustomHooks/useToggle";
import { LAYERS_INFO, handleLayerToggle } from "./MapLayers";

import MapLayerController from "./MapLayerController";
import MapCloseButton from "./MapCloseButton";
import OfficerClusterer from "./OfficerClusterer";
import OfficerSearch from "./OfficerSearch";
import {
  useMapClickedInfoWindow,
  MapClickedInfoWindow,
} from "./MapClickedInfoWindow";

import { controlButtonDiv, controlButtonWrapper } from "./MapButtonStyling";
import { OfficerLocationProvider } from "./OfficerLocationContext";

Geocode.setApiKey(import.meta.env.VITE_GOOGLE_MAPS_API_KEY);
Geocode.setLanguage("en");
Geocode.setRegion("us");
Geocode.setLocationType("ROOFTOP");
// Geocode.enableDebug();

const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  mapTypeControl: true,
};

const DashboardMap = ({ avlEnabled, user, subscriptionServer }) => {
  const { oriIds, userId } = useParams();
  const [isOpen, setIsOpen] = useState(null);
  const { responseErrorHandler, onlineState } = useContext(UserContext);
  const [incidentLocations, setIncidentLocations] = useState({});
  const [mappableOfficers, setMappableOfficers] = useState(null);
  const [polling, setPolling] = useState(true);
  const [center, setCenter] = useState(null);
  const [showIncidents, toggleShowIncidents] = useToggle(true);
  const [showOfficers, toggleShowOfficers] = useToggle(true);
  const [showOfficerAccuracy, toggleShowOfficerAccuracy] = useToggle(false);
  const [openMapMenu, setOpenMapMenu] = useState(null);
  const showSearch = openMapMenu === "officer";
  const toggleShowSearch = value => {
    setOpenMapMenu(prev =>
      value === false || prev === "officer" ? null : "officer",
    );
  };

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
  });

  const {
    handleMapClick,
    clickedLocation,
    clickedAddress,
    showClickedInfo,
    setShowClickedInfo,
    setClickedLocation,
    setClickedAddress,
  } = useMapClickedInfoWindow();

  const [
    getOfficers,
    { loading: mappableOfficersLoading, data: mappableOfficersData },
  ] = useLazyQuery(MAPPABLE_OFFICERS, {
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "no-cache",
    variables: { originatorId: userId, oriIds: oriIds },
    onError: error => {
      console.error(JSON.stringify(error) + " from DashboardMap.js");
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        setMappableOfficers(data.mappableOfficers);
      }
    },
  });

  const [getActiveIncidents, { data: activeIncidentsData }] = useLazyQuery(
    ACTIVE_INCIDENT_QUERY,
    {
      context: { serializationKey: "incidentQueue" },
      variables: {
        originatorId: user?.id,
        oriIds: oriIds,
        homeOriId: user?.homeOriId,
      },
      fetchPolicy: "no-cache",
      pollInterval: 8000,
      onError: error => {
        console.error(JSON.stringify(error));
      },
      onCompleted: data => {
        if (data) {
          responseErrorHandler(data);
        }
      },
    },
  );

  const latLngRef = useRef({});
  const processIncidentAddresses = useCallback(async data => {
    let addressObj = {};

    if (data && data[Object.keys(data)[0]]) {
      data[Object.keys(data)[0]].forEach(incident => {
        const { id, name, lat, lng } = incident?.address || {};
        if (name || (lat && lng)) {
          addressObj[id] = {
            lat: lat,
            lng: lng,
            name: name,
            incidentNumber: incident?.incidentNumber,
            classification: incident?.classification?.name,
            incidentId: incident?.id,
          };
        }
      });
    }

    let addressKeyArr = Object.keys(addressObj);
    for (let index = 0; index < addressKeyArr.length; index++) {
      if (
        !addressObj[addressKeyArr[index]]?.lat ||
        !addressObj[addressKeyArr[index]]?.lng
      ) {
        if (latLngRef.current?.[addressKeyArr[index]]) {
          addressObj[addressKeyArr[index]].lat =
            latLngRef.current[addressKeyArr[index]].lat;
          addressObj[addressKeyArr[index]].lng =
            latLngRef.current[addressKeyArr[index]].lng;
        } else {
          console.log(
            "Geocoding address:",
            addressObj[addressKeyArr[index]]?.name,
          );
          let latLng = await geocodeFromAddress(
            addressObj[addressKeyArr[index]]?.name,
          );
          if (!latLng.err) {
            addressObj[addressKeyArr[index]].lat = latLng.lat;
            addressObj[addressKeyArr[index]].lng = latLng.lng;
            const refObj = {
              [addressKeyArr[index]]: { lat: latLng.lat, lng: latLng.lng },
            };
            latLngRef.current = { ...latLngRef.current, ...refObj };
          } else {
            delete addressObj[addressKeyArr[index]];
          }
        }
      }
    }

    return addressObj;
  }, []);

  const setMapBoundsFromLocations = useCallback(
    locations => {
      if (
        Object.keys(locations).length > 0 &&
        window.google &&
        window.google.maps &&
        window.google.maps.LatLngBounds
      ) {
        const bounds = new window.google.maps.LatLngBounds();
        for (var loc in locations) {
          const inst = locations[loc];
          const latLng = new window.google.maps.LatLng(inst.lat, inst.lng);
          bounds.extend(latLng);
        }
        !center && setCenter(bounds.getCenter());
      }
    },
    [center],
  );

  const [needOfficerCenter, setNeedOfficerCenter] = useState(false);

  useEffect(() => {
    if (activeIncidentsData) {
      const fetchData = async () => {
        const addresses = await processIncidentAddresses(activeIncidentsData);
        if (Object.keys(addresses).length > 0) {
          setMapBoundsFromLocations(addresses);
          setIncidentLocations(addresses);
        } else {
          if (!center) {
            setNeedOfficerCenter(true);
          }
        }
      };
      fetchData();
    }
  }, [
    activeIncidentsData,
    center,
    processIncidentAddresses,
    setMapBoundsFromLocations,
  ]);

  useEffect(() => {
    const findCenterForOfficerOrOri = async () => {
      const u = mappableOfficers.find(o => +o?.id === user?.id);
      if (avlEnabled && user?.onDuty && u?.lat && u?.lng) {
        return { lat: parseFloat(u.lat), lng: parseFloat(u.lng) };
      } else {
        if (user?.ori?.oriAddress?.lat && user?.ori?.oriAddress?.lng) {
          return {
            lat: user?.ori?.oriAddress?.lat,
            lng: user?.ori?.oriAddress?.lng,
          };
        } else if (user?.ori?.oriAddress?.name) {
          try {
            const response = await Geocode.fromAddress(
              user?.ori?.oriAddress?.name,
            );
            const { lat, lng } = response.results[0].geometry.location;
            return { lat: lat, lng: lng };
          } catch (error) {
            console.error(error);
            alert("Failed to fetch coordinates from address");
            return { lat: 44.4752228, lng: -73.2078055 };
          }
        } else {
          alert("Home ORI address not found");
          return { lat: 44.4752228, lng: -73.2078055 };
        }
      }
    };

    if (
      needOfficerCenter &&
      Array.isArray(mappableOfficers) &&
      !mappableOfficersLoading &&
      mappableOfficersData
    ) {
      const getCenter = async () => {
        const newCenter = await findCenterForOfficerOrOri();
        setCenter(newCenter);
        setNeedOfficerCenter(false);
      };
      getCenter();
    }
  }, [
    mappableOfficers,
    needOfficerCenter,
    mappableOfficersData,
    mappableOfficersLoading,
    avlEnabled,
    user?.onDuty,
    user?.id,
    user?.ori?.oriAddress?.lat,
    user?.ori?.oriAddress?.lng,
    user?.ori?.oriAddress?.name,
  ]);

  const geocodeFromAddress = async addressName => {
    let error;
    const response = await Geocode.fromAddress(addressName).catch(
      err => (error = err),
    );
    if (response && !error) {
      const { lat, lng } = response.results[0].geometry.location;
      return { lat: lat, lng: lng };
    } else {
      return { err: error };
    }
  };

  const makeIncidentMarkers = () => {
    if (Object.keys(incidentLocations).length > 0) {
      return (
        <MarkerClusterer>
          {clusterer =>
            Object.keys(incidentLocations).map(
              (key, index) =>
                incidentLocations[key]?.lat && (
                  <Marker
                    label={incidentLocations[key]?.incidentNumber}
                    key={"incident-" + index}
                    position={{
                      lat: incidentLocations[key]?.lat,
                      lng: incidentLocations[key]?.lng,
                    }}
                    clusterer={clusterer}
                    onClick={() =>
                      setIsOpen(incidentLocations[key]?.incidentId)
                    }
                  >
                    {isOpen === incidentLocations[key]?.incidentId && (
                      <InfoWindow onCloseClick={() => setIsOpen(false)}>
                        <div
                          style={{ display: "flex", flexDirection: "column" }}
                        >
                          <span>{incidentLocations[key]?.incidentNumber}</span>
                          <span>{incidentLocations[key]?.classification}</span>
                          <span>{incidentLocations[key]?.name}</span>
                        </div>
                      </InfoWindow>
                    )}
                  </Marker>
                ),
            )
          }
        </MarkerClusterer>
      );
    } else {
      return null;
    }
  };

  useEffect(() => {
    window.addEventListener("focus", () => setPolling(true));
    window.addEventListener("blur", () => setPolling(false));
    return () => {
      window.removeEventListener("focus", () => setPolling(true));
      window.removeEventListener("blur", () => setPolling(false));
    };
  });

  useEffect(() => {
    if (import.meta.env.VITE_USE_POLLING.toLowerCase() === "true") {
      const interval = setInterval(() => {
        if (onlineState && user?.isAuthenticated && user?.id && polling) {
          // console.log("Polling for incidents and officers on the DashboardMap");
          // Now using the subscription server for real-time updates
          getOfficers({ variables: { noSessionReset: true } });
          getActiveIncidents();
        }
      }, 20000);
      return () => {
        clearInterval(interval);
      };
    }
  }, [
    getActiveIncidents,
    getOfficers,
    onlineState,
    polling,
    user?.id,
    user?.isAuthenticated,
  ]);

  useEffect(() => {
    getOfficers({ variables: { noSessionReset: true } });
    getActiveIncidents();
  }, [getActiveIncidents, getOfficers]);

  const mapRef = useRef(null);
  const [mapFitted, setMapFitted] = useState(false);

  // Fit map to all incident and officer markers after markers are rendered
  useEffect(() => {
    if (!mapRef.current) return;
    const timeout = setTimeout(() => {
      const bounds = new window.google.maps.LatLngBounds();

      // Incident markers
      Object.values(incidentLocations).forEach(loc => {
        if (loc.lat && loc.lng) {
          bounds.extend(
            new window.google.maps.LatLng(Number(loc.lat), Number(loc.lng)),
          );
        }
      });

      // Officer markers
      if (Array.isArray(mappableOfficers)) {
        mappableOfficers.forEach(officer => {
          if (officer.lat && officer.lng) {
            bounds.extend(
              new window.google.maps.LatLng(
                Number(officer.lat),
                Number(officer.lng),
              ),
            );
          }
        });
      }

      if (!bounds.isEmpty() && !mapFitted) {
        mapRef.current.fitBounds(bounds);
        setMapFitted(true);
      }
    }, 0);

    return () => clearTimeout(timeout);
  }, [incidentLocations, mappableOfficers, mapFitted]);

  // Reset mapFitted if data changes
  // useEffect(() => {
  //   setMapFitted(false);
  // }, [incidentLocations, mappableOfficers]);

  // Optionally, listen for user map interaction to stop auto-fitting
  useEffect(() => {
    if (!mapRef.current) return;
    const listener = mapRef.current.addListener("dragstart", () =>
      setMapFitted(true),
    );
    return () => {
      window.google.maps.event.removeListener(listener);
    };
  }, []);

  const onMapLoad = useCallback(map => {
    mapRef.current = map;
  }, []);

  const [findOfficer, setFindOfficer] = useState(null);

  const findOfficerLocation = (officerId, latLngObj) => {
    setFindOfficer(officerId);

    if (
      latLngObj &&
      typeof latLngObj.lat !== "undefined" &&
      typeof latLngObj.lng !== "undefined" &&
      Number.isFinite(Number(latLngObj.lat)) &&
      Number.isFinite(Number(latLngObj.lng)) &&
      mapRef.current.panTo
    ) {
      mapRef.current.panTo({
        lat: Number(latLngObj.lat),
        lng: Number(latLngObj.lng),
      });
    } else {
      console.error("Invalid latLngObj provided to findOfficerLocation");
      return;
    }
  };

  if (isLoaded && center) {
    return (
      <div style={{ height: "100vh", width: "100vw" }}>
        <OfficerLocationProvider
          officers={mappableOfficers}
          subscriptionServer={subscriptionServer}
        >
          <div style={controlButtonDiv}>
            <div style={controlButtonWrapper}>
              <MapLayerController
                options={Object.keys(LAYERS_INFO)
                  .filter(k => LAYERS_INFO[k]?.url)
                  // .sort()
                  .map(label => {
                    return { handler: handleLayerToggle, label: label };
                  })}
                map={mapRef.current}
                showIncidentToggle={true}
                toggleShowIncidents={toggleShowIncidents}
                showIncidents={showIncidents}
                showOfficerToggle={true}
                toggleShowOfficers={toggleShowOfficers}
                showOfficers={showOfficers}
                showOfficerAccuracy={showOfficerAccuracy}
                toggleShowOfficerAccuracy={toggleShowOfficerAccuracy}
                userId={user?.id}
                openMenu={openMapMenu}
                setOpenMenu={setOpenMapMenu}
              />

              <OfficerSearch
                showSearch={showSearch}
                toggleShowSearch={toggleShowSearch}
                findOfficerCallback={findOfficerLocation}
              />
            </div>
          </div>
          <MapCloseButton />
          <GoogleMap
            resetBoundsOnResize
            options={mapOptions}
            id="dashboard-map"
            mapContainerStyle={{
              height: "100%",
              width: "100%",
            }}
            zoom={15}
            defaultCenter={center}
            center={center}
            onLoad={onMapLoad}
            onClick={handleMapClick}
          >
            {user?.ori?.oriAddress?.lat && (
              <Marker
                icon={"http://maps.google.com/mapfiles/kml/pal2/icon0.png"}
                position={{
                  lat: user?.ori?.oriAddress?.lat,
                  lng: user?.ori?.oriAddress?.lng,
                }}
                onClick={() => setIsOpen(user?.ori.id)}
              >
                {isOpen === user?.ori.id && (
                  <InfoWindow onCloseClick={() => setIsOpen(false)}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span>{user?.ori?.oriName}</span>
                    </div>
                  </InfoWindow>
                )}
              </Marker>
            )}
            {showIncidents && makeIncidentMarkers()}
            {showOfficers && (
              <OfficerClusterer
                map={mapRef.current}
                officers={mappableOfficers}
                showOfficerAccuracy={showOfficerAccuracy}
                subscriptionServer={subscriptionServer}
                findOfficer={findOfficer}
                setFindOfficer={setFindOfficer}
              />
            )}
            <MapClickedInfoWindow
              clickedLocation={clickedLocation}
              clickedAddress={clickedAddress}
              showClickedInfo={showClickedInfo}
              setShowClickedInfo={setShowClickedInfo}
              setClickedLocation={setClickedLocation}
              setClickedAddress={setClickedAddress}
            />
          </GoogleMap>
        </OfficerLocationProvider>
      </div>
    );
  } else {
    return <div>Waiting</div>;
  }
};

export default DashboardMap;
